/**
 * The controls: buttons, fields, dropdowns, filters and states.
 * Styles in styles/base.css and styles/components.css; docs/design.md says
 * when to use which.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { Icon, type IconName } from "./icons";

export { Select, type SelectOption } from "../shell/Select";

export type ButtonVariant = "primary" | "secondary" | "quiet";

const VARIANT_CLASS: Record<ButtonVariant, string> = { primary: " primary", secondary: "", quiet: " ghost" };

export interface ButtonProps {
  /** primary: the one thing to do on a screen, in ink. secondary: the rest. quiet: leaving, and small actions in a row. */
  readonly variant?: ButtonVariant;
  readonly size?: "sm";
  readonly block?: boolean;
  readonly icon?: IconName;
  /** A link that looks like a button. */
  readonly href?: string;
  /**
   * The link leaves for another site: it opens in a new tab, carries no
   * opener and no referrer, is marked with the leaving icon, and says so to a
   * screen reader.
   */
  readonly external?: boolean;
  readonly type?: "button" | "submit";
  readonly disabled?: boolean;
  /** For a button that shows and hides something below it: whether that is shown now. */
  readonly expanded?: boolean;
  readonly onClick?: (event: MouseEvent) => void;
  readonly children: ComponentChildren;
}

/** A button, or a link that looks like one when given href. */
export function Button(props: ButtonProps) {
  const cls = `btn${VARIANT_CLASS[props.variant ?? "secondary"]}${props.size === "sm" ? " sm" : ""}${props.block ? " block" : ""}`;
  const content = (
    <>
      {props.icon ? <Icon name={props.icon} /> : null}
      {props.children}
    </>
  );
  if (props.href !== undefined) {
    if (props.external) {
      return (
        <a class={cls} href={props.href} target="_blank" rel="noopener noreferrer" onClick={props.onClick}>
          {content}
          <Icon name="out" />
          <span class="sr">(opens in a new tab)</span>
        </a>
      );
    }
    return (
      <a class={cls} href={props.href} onClick={props.onClick}>
        {content}
      </a>
    );
  }
  return (
    <button type={props.type ?? "button"} class={cls} disabled={props.disabled} aria-expanded={props.expanded} onClick={props.onClick}>
      {content}
    </button>
  );
}

/** A button that is only an icon. Its name is read out, and shown on hover. */
export function IconButton(props: { readonly icon: IconName; readonly label: string; readonly onClick?: () => void; readonly href?: string }) {
  if (props.href) {
    return (
      <a class="icon-btn" href={props.href} aria-label={props.label} title={props.label}>
        <Icon name={props.icon} />
      </a>
    );
  }
  return (
    <button type="button" class="icon-btn" aria-label={props.label} title={props.label} onClick={props.onClick}>
      <Icon name={props.icon} />
    </button>
  );
}

/**
 * A labelled field. The label, the hint and the error are tied to the control
 * by id, so a screen reader reads all three; an error marks the control
 * invalid. Children render instead of the text input, for a Select.
 */
export function Field(props: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly multiline?: boolean;
  readonly type?: string;
  readonly value?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly onInput?: (value: string) => void;
  readonly children?: ComponentChildren;
}) {
  const hintId = props.hint ? `${props.id}-hint` : undefined;
  const errorId = props.error ? `${props.id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const common = {
    id: props.id,
    class: "input",
    value: props.value,
    placeholder: props.placeholder,
    disabled: props.disabled,
    "aria-describedby": describedBy,
    "aria-invalid": props.error ? ("true" as const) : undefined,
    onInput: (event: Event) => props.onInput?.((event.currentTarget as HTMLInputElement).value),
  };
  return (
    <div class="field">
      <label for={props.id}>{props.label}</label>
      {props.children ?? (props.multiline ? <textarea rows={3} {...common} /> : <input type={props.type ?? "text"} {...common} />)}
      {props.hint ? (
        <span class="hint" id={hintId}>
          {props.hint}
        </span>
      ) : null}
      {props.error ? (
        <span class="error" id={errorId}>
          {props.error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Filters, as real checkboxes in a row: which kinds of mark to show, which
 * sources to search. Never pill shaped toggles (Mohit, 19 October 2026: they
 * read as AI made).
 */
export function Checks<T extends string>(props: {
  readonly label: string;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly checked: readonly T[];
  readonly onChange: (checked: T[]) => void;
}) {
  const toggle = (value: T) =>
    props.onChange(props.checked.includes(value) ? props.checked.filter((v) => v !== value) : [...props.checked, value]);
  return (
    <fieldset class="checks">
      <legend class="sr">{props.label}</legend>
      {props.options.map((option) => (
        <label key={option.value}>
          <input type="checkbox" checked={props.checked.includes(option.value)} onChange={() => toggle(option.value)} />
          {option.label}
        </label>
      ))}
    </fieldset>
  );
}

export type StatusTone = "neutral" | "done" | "attn" | "ok" | "bad";

/**
 * A state, in words, in the state's colour: "In review", "8 changes to
 * review", "Paid". No outline, no dot, no fill (Mohit, 19 October 2026).
 */
export function Status(props: { readonly tone?: StatusTone; readonly children: ComponentChildren }) {
  const tone = props.tone && props.tone !== "neutral" ? ` ${props.tone}` : "";
  return <span class={`status${tone}`}>{props.children}</span>;
}

/**
 * Every filter of a list in one place (Mohit, 29 October 2026: "all filters at
 * one place, if I click filter, then it shows every option and I can choose it
 * and save it"; no segmented switches, dropdowns instead). A button names how
 * many filters are set; pressing it opens a panel of every option, chosen as
 * dropdowns and checkboxes. Nothing applies until Save: Cancel, Escape and a
 * press outside leave the list as it was, and Reset puts every option back.
 *
 * The page keeps the draft: onOpen copies what is applied into it, the
 * children edit it, onSave applies it. The panel stays inside the screen at
 * any width, its place set from script because the Content Security Policy
 * refuses inline styles.
 */
export function FilterPanel(props: {
  readonly id: string;
  /** How many filters differ from their defaults, said on the button. */
  readonly active: number;
  readonly onOpen: () => void;
  readonly onSave: () => void;
  /** Puts the draft back to the defaults; the panel stays open to be saved. */
  readonly onReset: () => void;
  /** Drawn open, for the component sheet. */
  readonly open?: boolean;
  readonly children: ComponentChildren;
}) {
  const [open, setOpen] = useState(props.open ?? false);
  const wrap = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  useEffect(() => {
    if (!open || props.open) return;
    const outside = (event: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) close(false);
    };
    const escape = (event: KeyboardEvent) => {
      // A dropdown inside closes itself first; only then does Escape close the panel.
      if (event.key === "Escape" && !panel.current?.querySelector(".select.is-open")) close(true);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open, props.open]);

  // Inside the screen, 16 pixels from either edge, under the button.
  useLayoutEffect(() => {
    const box = panel.current;
    const anchor = wrap.current;
    if (!open || !box || !anchor || props.open) return;
    const place = () => {
      const width = Math.min(360, window.innerWidth - 32);
      const at = anchor.getBoundingClientRect();
      const left = Math.min(Math.max(16, at.right - width), window.innerWidth - 16 - width);
      box.style.width = `${width}px`;
      box.style.left = `${left - at.left}px`;
    };
    place();
    box.querySelector<HTMLElement>("button, input")?.focus();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, props.open]);

  return (
    <div class="filters" ref={wrap}>
      <button
        ref={button}
        type="button"
        class="btn sm filters-button"
        aria-expanded={open}
        aria-controls={`${props.id}-panel`}
        onClick={() => {
          if (open) return close(false);
          props.onOpen();
          setOpen(true);
        }}
      >
        <Icon name="filter" />
        {props.active > 0 ? `Filters (${props.active})` : "Filters"}
      </button>
      <div
        ref={panel}
        id={`${props.id}-panel`}
        class={props.open ? "filter-panel inline" : "filter-panel"}
        role="dialog"
        aria-label="Filters"
        hidden={!open}
      >
        <div class="filter-body">{props.children}</div>
        <div class="filter-foot">
          <Button variant="quiet" size="sm" onClick={props.onReset}>
            Reset
          </Button>
          <span class="grow" />
          <Button size="sm" onClick={() => close(true)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              props.onSave();
              close(true);
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
